import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isShoppingCartPage } from 'helpers/donate';
import { useAppDispatch } from 'helpers/hooks';
import type { PaymentProcessor } from 'helpers/mixpanelEvents';
import { getUrlParams } from 'helpers/utils';
import { completeSavedCartOnce } from 'services/completeSavedCart';
import { IPage } from 'store/donatePage/types';
import { IEvent } from 'store/eventPage/types';

interface Options {
  page?: IPage | IEvent | null;
  paymentProcessor: PaymentProcessor;
}

/**
 * Closes the saved cart the donor checked out from, when `shouldComplete`. Returns that cart's id,
 * else undefined, for the confirmation event's `cart_id`, so the event and /complete share one rule.
 */
export const useCompleteSavedCart = (
  shouldComplete: boolean,
  { page, paymentProcessor }: Options
): string | undefined => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  // donor_cart outlives its checkout in the URL; only SearchScreen's Donate reaches a cart page's
  // confirmation, and it rewrites the param, so a non-cart page's donor_cart is a stale one.
  const cartId =
    shouldComplete && isShoppingCartPage(page) ? getUrlParams().urlDonorCart : undefined;

  useEffect(() => {
    // While Auth0 loads, isAuthenticated reads false; sending then would claim the cart id without
    // the JWT a logged-in donor's cart needs.
    if (!cartId || isLoading) return;

    const jwt = isAuthenticated
      ? getAccessTokenSilently().catch(() => undefined)
      : Promise.resolve(undefined);
    jwt.then(token => completeSavedCartOnce(dispatch, { cartId, jwt: token, paymentProcessor }));
  }, [cartId, isLoading, isAuthenticated, paymentProcessor, dispatch, getAccessTokenSilently]);

  return cartId;
};
